import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2, Clock, Loader2, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { WebsiteHeader } from '../components/Dashboard/WebsiteHeader';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { isCheckoutPlan } from '../services/checkoutService';
import { notifyCreditsChanged } from '../utils/creditsSync';
import { clearPendingUnlockScanId, readPendingUnlockScanId } from '../hooks/useReportUnlock';
import type { PlanType } from '../types/payment';
type ReturnStatus = 'waiting_auth' | 'confirming' | 'confirmed' | 'timed_out' | 'signed_out';
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 60_000;

/**
 * Landing page after the payment provider redirects back.
 * The URL only tells us WHAT to check — access is granted solely when the
 * database reports scans.is_unlocked === true or profiles.is_pro === true.
 */
export function PaymentReturnPage() {
  const navigate = useNavigate();
  const {
    user,
    loading: authLoading,
    refreshUser
  } = useAuth();
  const [{
    plan,
    scanId
  }] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const rawPlan = params.get('plan');
    const parsedPlan: PlanType | null = isCheckoutPlan(rawPlan) ? rawPlan : null;
    const isSubscription = parsedPlan !== null && parsedPlan !== 'single_scan';
    const id = isSubscription ? null : params.get('scan_id') || readPendingUnlockScanId();
    return {
      plan: parsedPlan,
      scanId: id
    };
  });
  const mode: 'scan' | 'subscription' = scanId ? 'scan' : 'subscription';
  const [status, setStatus] = useState<ReturnStatus>('waiting_auth');
  const generationRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  const startedRef = useRef(false);
  // refreshUser isn't memoized upstream — keep a ref so polling never restarts.
  const refreshUserRef = useRef(refreshUser);
  refreshUserRef.current = refreshUser;
  useEffect(() => {
    document.title = 'Confirming payment - Toxic Detector';
  }, []);
  const stop = useCallback(() => {
    generationRef.current += 1;
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);
  const isConfirmedInDatabase = useCallback(async (userId: string): Promise<boolean> => {
    if (mode === 'scan' && scanId) {
      const {
        data,
        error
      } = await supabase.from('scans').select('id, is_unlocked').eq('id', scanId).maybeSingle();
      return !error && data?.is_unlocked === true;
    }
    const {
      data,
      error
    } = await supabase.from('profiles').select('id, is_pro').eq('id', userId).maybeSingle();
    return !error && data?.is_pro === true;
  }, [mode, scanId]);
  const poll = useCallback((userId: string) => {
    stop();
    const generation = generationRef.current;
    const startedAt = Date.now();
    setStatus('confirming');
    const tick = async () => {
      let confirmed = false;
      try {
        confirmed = await isConfirmedInDatabase(userId);
      } catch {
        confirmed = false;
      }
      if (generation !== generationRef.current) return;
      if (confirmed) {
        stop();
        setStatus('confirmed');
        clearPendingUnlockScanId();
        notifyCreditsChanged();
        void refreshUserRef.current();
        return;
      }
      if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
        stop();
        setStatus('timed_out');
        return;
      }
      timerRef.current = window.setTimeout(() => void tick(), POLL_INTERVAL_MS);
    };
    void tick();
  }, [isConfirmedInDatabase, stop]);

  // Wait for the session to restore after the redirect, then poll once.
  useEffect(() => {
    if (authLoading || startedRef.current) return;
    if (!user?.id) {
      setStatus('signed_out');
      return;
    }
    startedRef.current = true;
    poll(user.id);
  }, [authLoading, user?.id, poll]);
  useEffect(() => stop, [stop]);

  // Once the database confirms a report unlock, return to that report.
  useEffect(() => {
    if (status !== 'confirmed' || mode !== 'scan' || !scanId) return;
    const timer = window.setTimeout(() => {
      navigate(`/analysis?id=${encodeURIComponent(scanId)}&checkout=return`, {
        replace: true
      });
    }, 900);
    return () => window.clearTimeout(timer);
  }, [status, mode, scanId, navigate]);
  const reportPath = scanId ? `/analysis?id=${encodeURIComponent(scanId)}` : '/dashboard';
  const planName = plan === 'weekly' ? 'Weekly' : plan === 'monthly' ? 'Monthly' : plan === 'yearly' ? 'Yearly' : 'Pro';
  return <div className="flex min-h-screen w-full flex-col bg-[#050505] text-white" data-id="element-9265" data-mp="14r5cnalxb6jn">
      <WebsiteHeader data-id="element-9266" data-mp="2b6pn0n1e3uzi" />
      <main className="mx-auto w-full max-w-md flex-1 px-5 pb-10 pt-28" data-id="element-9267" data-mp="2b5efqd8qk53n">
        <div role="status" aria-live="polite" className="border border-white/10 bg-[#0a0a0a] p-8 text-center" data-id="element-9268" data-mp="2g4rbxolhyoiw">
          {status === 'confirmed' ? <CheckCircle2 className="mx-auto text-emerald-400" size={36} aria-hidden="true" data-id="element-9269" data-mp="3vogs5a0cvgsg" /> : status === 'timed_out' ? <Clock className="mx-auto text-amber-300" size={36} aria-hidden="true" data-id="element-9270" data-mp="3qw78uctqxez5" /> : <Loader2 className="mx-auto animate-spin text-[#FF0000]" size={36} aria-hidden="true" data-id="element-9271" data-mp="3lxm90eieoxb8" />}

          <h1 className="mt-4 text-2xl font-black uppercase tracking-tight" data-id="element-9272" data-mp="3h0ng1zahawem">
            {status === 'confirmed' ? mode === 'scan' ? 'Report unlocked' : `${planName} plan active` : status === 'timed_out' ? 'Still confirming' : status === 'signed_out' ? 'Sign in to continue' : 'Confirming your payment…'}
          </h1>

          <p className="mt-2 text-sm leading-relaxed text-gray-400" data-id="element-9273" data-mp="117oha9lky67c">
            {status === 'confirmed' ? mode === 'scan' ? 'Returning to your report…' : 'Your Pro access is ready.' : status === 'timed_out' ? 'If you were charged, it will unlock shortly.' : status === 'signed_out' ? 'Sign in to the account you paid with and we’ll confirm your purchase.' : 'This usually takes a few seconds. Please keep this page open.'}
          </p>

          {status === 'timed_out' && <div className="mt-6 flex flex-col gap-3" data-id="element-9274" data-mp="3jeiro2k30n4s">
              <button type="button" onClick={() => user?.id && poll(user.id)} className="inline-flex min-h-[44px] items-center justify-center gap-2 bg-[#FF0000] px-4 text-xs font-bold uppercase tracking-widest text-white transition-colors duration-150 ease-out hover:bg-[#D90000]" data-id="element-9275" data-mp="3jbwm28gr4hp7">
                <RefreshCw size={14} aria-hidden="true" data-id="element-9276" data-mp="1y0wkcys3abgn" />
                Check again
              </button>
              <button type="button" onClick={() => navigate(reportPath)} className="min-h-[44px] border border-white/10 px-4 text-xs font-bold uppercase tracking-widest text-gray-300 transition-colors duration-150 ease-out hover:border-white/30 hover:text-white" data-id="element-9277" data-mp="g1ylkook85o1">
                {scanId ? 'Back to report' : 'Go to dashboard'}
              </button>
            </div>}

          {status === 'confirmed' && mode === 'subscription' && <button type="button" onClick={() => navigate('/dashboard', {
          replace: true
        })} className="mt-6 inline-flex min-h-[44px] w-full items-center justify-center bg-[#FF0000] px-4 text-xs font-bold uppercase tracking-widest text-white transition-colors duration-150 ease-out hover:bg-[#D90000]" data-id="element-9278" data-mp="3ok4fd3dpf3bt">
              Go to dashboard
            </button>}

          {status === 'signed_out' && <button type="button" onClick={() => navigate(`/auth?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`)} className="mt-6 inline-flex min-h-[44px] w-full items-center justify-center bg-[#FF0000] px-4 text-xs font-bold uppercase tracking-widest text-white transition-colors duration-150 ease-out hover:bg-[#D90000]" data-id="element-9279" data-mp="3une1kkzcve3w">
              Sign in
            </button>}
        </div>
      </main>
    </div>;
}